import { type ChartOptions } from "chart.js";

import { KpiType } from "@features/mmm/components/explore-budgets/client-setting/types";
import { type User } from "@internal/user";
import { MetricFormatters } from "@north-beam/nb-client-side";

import { chartTooltip } from "./chart-tooltip";
import { REVENUE_ID, ROAS_ID, SPEND_ID } from "./const";
import {
  type YScaleTitlesPluginPluginOptions,
  Y_SCALE_TITLES_PLUGIN_ID,
} from "./plugins/y-scale-titles-plugin";

export const getChartOptions = (
  user: User,
  kpiType: KpiType,
): ChartOptions<"line"> => {
  const isRevenue = kpiType === KpiType.REVENUE;

  return {
    responsive: true,
    maintainAspectRatio: true,
    interaction: {
      mode: "index" as const,
      intersect: false,
      position: "nearest",
    },
    animation: false,
    plugins: {
      legend: { display: false },
      zoom: {
        zoom: {
          wheel: {
            modifierKey: "alt",
            enabled: true,
          },
          drag: {
            modifierKey: "alt",
            enabled: true,
          },
          mode: "x",
        },
      },
      tooltip: {
        enabled: false,
        external: chartTooltip,
        user,
        isRevenue,
      },
      annotation: {
        clip: false,
        common: { drawTime: "beforeDraw" },
      },
    },
    layout: { padding: { left: 16, right: 20 } },
    [Y_SCALE_TITLES_PLUGIN_ID]: {
      font: {
        family: "Inter",
        size: 12,
        weight: "600",
      },
      titles: {},
    } as YScaleTitlesPluginPluginOptions,
    scales: {
      [REVENUE_ID]: {
        type: "linear" as const,
        suggestedMax: 5,
        axis: "y",
        position: "right" as const,
        grace: "50%",
        beginAtZero: true,
        title: {
          display: false,
          padding: 0,
        },
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: true,
        },
        ticks: {
          maxTicksLimit: 6,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
          callback: (val) =>
            isRevenue
              ? MetricFormatters.currency(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                )
              : MetricFormatters.decimal(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                ),
        },
      },
      [SPEND_ID]: {
        type: "linear" as const,
        suggestedMax: 5,
        axis: "y",
        position: "right" as const,
        grace: "50%",
        beginAtZero: true,
        title: {
          display: false,
          padding: 0,
        },
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: true,
        },
        ticks: {
          maxTicksLimit: 6,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
          callback: (val) =>
            MetricFormatters.currency(
              Number(val),
              user.currencies.currentCurrency,
              {
                precision: "approximate",
              },
            ),
        },
      },
      [ROAS_ID]: {
        type: "linear" as const,
        axis: "y",
        position: "left" as const,
        beginAtZero: true,
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: false,
        },
        ticks: {
          maxTicksLimit: 6,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
          callback: (val) =>
            isRevenue
              ? MetricFormatters.multiplier(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                )
              : MetricFormatters.currency(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                ),
        },
      },
      x: {
        axis: "x",
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
          drawTicks: false,
        },
        ticks: {
          padding: 20,
          maxRotation: 0,
          autoSkip: true,
          font: {
            family: "Commons Pro",
            size: 13,
          },
        },
      },
    },
  } as ChartOptions<"line">;
};
